(() => {
  const rotationMs = 10000;
  const marqueeSpeedPxPerSecond = 15;
  const maxInteractiveSpeedPxPerSecond = 1200;

  const initialiseInfiniteMotion = (card, interactionSurface, track, getDistancePx) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    card.classList.add('deal-social-proof--physics');

    let activePointerId = null;
    let positionPx = 0;
    let velocityPxPerSecond = -marqueeSpeedPxPerSecond;
    let previousX = 0;
    let previousTime = 0;
    let previousFrameTime = 0;
    let previousWheelTime = 0;
    let wheelActive = false;
    let wheelReleaseTimer = 0;

    const wrapPosition = () => {
      const distancePx = getDistancePx();
      if (distancePx <= 0) return;

      while (positionPx <= -distancePx) positionPx += distancePx;
      while (positionPx > 0) positionPx -= distancePx;
    };

    const render = () => {
      wrapPosition();
      track.style.transform = `translate3d(${positionPx}px, 0, 0)`;
    };

    const animate = (frameTime) => {
      if (!card.isConnected) return;
      if (!previousFrameTime) previousFrameTime = frameTime;
      const deltaSeconds = Math.min((frameTime - previousFrameTime) / 1000, 0.032);
      previousFrameTime = frameTime;

      if (activePointerId === null && !wheelActive) {
        const returnToCruise = 1 - Math.exp(-2.4 * deltaSeconds);
        velocityPxPerSecond += (
          -marqueeSpeedPxPerSecond - velocityPxPerSecond
        ) * returnToCruise;
        positionPx += velocityPxPerSecond * deltaSeconds;
        render();
      }

      window.requestAnimationFrame(animate);
    };

    const finishPointerInteraction = (pointerId = null, releaseCapture = true) => {
      if (activePointerId === null || (pointerId !== null && pointerId !== activePointerId)) return;

      const releasedPointerId = activePointerId;
      activePointerId = null;
      card.classList.remove('deal-social-proof--dragging');

      if (releaseCapture && interactionSurface.hasPointerCapture(releasedPointerId)) {
        interactionSurface.releasePointerCapture(releasedPointerId);
      }
    };

    const release = (event) => finishPointerInteraction(event.pointerId);

    interactionSurface.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;

      window.clearTimeout(wheelReleaseTimer);
      wheelActive = false;
      activePointerId = event.pointerId;
      previousX = event.clientX;
      previousTime = event.timeStamp;
      velocityPxPerSecond = 0;
      card.classList.add('deal-social-proof--dragging');
      interactionSurface.setPointerCapture(event.pointerId);
    });

    interactionSurface.addEventListener('pointermove', (event) => {
      if (event.pointerId !== activePointerId) return;
      if (event.pointerType === 'mouse' && event.buttons === 0) {
        finishPointerInteraction(event.pointerId);
        return;
      }

      const elapsedSeconds = Math.max((event.timeStamp - previousTime) / 1000, 0.008);
      const deltaPx = event.clientX - previousX;
      positionPx += deltaPx;
      velocityPxPerSecond = Math.max(
        -maxInteractiveSpeedPxPerSecond,
        Math.min(maxInteractiveSpeedPxPerSecond, deltaPx / elapsedSeconds),
      );
      previousX = event.clientX;
      previousTime = event.timeStamp;
      render();
    });

    interactionSurface.addEventListener('pointerup', release);
    interactionSurface.addEventListener('pointercancel', release);
    interactionSurface.addEventListener('lostpointercapture', (event) => {
      finishPointerInteraction(event.pointerId, false);
    });
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', release);
    window.addEventListener('blur', () => finishPointerInteraction(null, false));
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) finishPointerInteraction(null, false);
    });

    interactionSurface.addEventListener('wheel', (event) => {
      const horizontalDelta = Math.abs(event.deltaX) >= Math.abs(event.deltaY)
        ? event.deltaX
        : event.shiftKey
          ? event.deltaY
          : 0;
      if (!horizontalDelta || activePointerId !== null) return;

      event.preventDefault();
      const deltaMultiplier = event.deltaMode === 1
        ? 16
        : event.deltaMode === 2
          ? interactionSurface.clientWidth
          : 1;
      const deltaPx = horizontalDelta * deltaMultiplier * 0.55;
      const elapsedSeconds = previousWheelTime
        ? Math.max((event.timeStamp - previousWheelTime) / 1000, 0.008)
        : 0.016;

      window.clearTimeout(wheelReleaseTimer);
      wheelActive = true;
      previousWheelTime = event.timeStamp;
      card.classList.add('deal-social-proof--dragging');
      positionPx -= deltaPx;
      velocityPxPerSecond = Math.max(
        -maxInteractiveSpeedPxPerSecond,
        Math.min(maxInteractiveSpeedPxPerSecond, -deltaPx / elapsedSeconds),
      );
      render();

      wheelReleaseTimer = window.setTimeout(() => {
        previousWheelTime = 0;
        wheelActive = false;
        card.classList.remove('deal-social-proof--dragging');
      }, 90);
    }, {passive: false});

    render();
    window.requestAnimationFrame(animate);
  };

  const initialiseMarquee = (card, entries) => {
    const entriesContainer = card.querySelector('.deal-social-proof__entries');
    if (!entriesContainer) return;

    card.classList.add('deal-social-proof--marquee');
    entries.forEach((entry) => {
      entry.hidden = false;
    });

    const track = document.createElement('div');
    track.className = 'deal-social-proof__marquee-track';

    const group = document.createElement('div');
    group.className = 'deal-social-proof__marquee-group';
    entries.forEach((entry) => group.append(entry));

    const duplicate = group.cloneNode(true);
    duplicate.setAttribute('aria-hidden', 'true');
    duplicate.querySelectorAll('[data-social-proof-entry]').forEach((entry) => {
      entry.removeAttribute('data-social-proof-entry');
    });

    track.append(group, duplicate);
    entriesContainer.replaceChildren(track);
    initialiseInfiniteMotion(
      card,
      entriesContainer,
      track,
      () => group.getBoundingClientRect().width,
    );

    const updateMarqueeDuration = () => {
      const distancePx = group.getBoundingClientRect().width;
      if (distancePx <= 0) return;

      track.style.setProperty(
        '--deal-social-proof-marquee-duration',
        `${distancePx / marqueeSpeedPxPerSecond}s`,
      );
    };

    updateMarqueeDuration();

    if (document.fonts?.ready) {
      document.fonts.ready.then(updateMarqueeDuration);
    }

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(updateMarqueeDuration);
      resizeObserver.observe(group);
      card.dealSocialProofResizeObserver = resizeObserver;
    }
  };

  const initialise = (card) => {
    if (card.dataset.socialProofInitialised === 'true') return;

    const entries = [...card.querySelectorAll('[data-social-proof-entry]')];
    if (!entries.length) return;

    card.dataset.socialProofInitialised = 'true';

    if (!card.classList.contains('deal-social-proof--media-overlay')) {
      initialiseMarquee(card, entries);
      return;
    }

    let activeIndex = Math.max(entries.findIndex((entry) => !entry.hidden), 0);
    entries.forEach((entry, index) => {
      entry.hidden = index !== activeIndex;
    });

    if (entries.length < 2) return;

    window.setInterval(() => {
      entries[activeIndex].hidden = true;
      activeIndex = (activeIndex + 1) % entries.length;
      entries[activeIndex].hidden = false;
    }, rotationMs);
  };

  const start = () => document.querySelectorAll('[data-deal-social-proof]').forEach(initialise);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {once: true});
  } else {
    start();
  }
})();
